import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Users, Plus, Pencil, Trash2, X, Check } from "lucide-react";
import { money } from "../lib/calc";
import { toast } from "sonner";
import ConfirmDialog from "../components/ConfirmDialog";

const empty = { name: "", project_id: "none", weekly_salary: "", notes: "" };

export default function WorkerDialog({ workers, projects, onCreate, onUpdate, onDelete }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [delId, setDelId] = useState(null);

  const reset = () => { setForm(empty); setEditing(null); };

  const submit = async () => {
    if (!form.name.trim()) return toast.error("El nombre es obligatorio");
    const payload = {
      name: form.name.trim(),
      project_id: form.project_id === "none" ? null : form.project_id,
      weekly_salary: Number(form.weekly_salary) || 0,
      notes: form.notes,
    };
    if (editing) await onUpdate(editing, payload);
    else await onCreate(payload);
    reset();
  };

  const startEdit = (w) => {
    setEditing(w.id);
    setForm({ name: w.name, project_id: w.project_id || "none", weekly_salary: w.weekly_salary || "", notes: w.notes || "" });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" data-testid="open-workers" className="border-slate-300">
          <Users size={15} className="mr-1.5" /> Trabajadores
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl rounded-sm">
        <DialogHeader><DialogTitle className="font-heading tracking-tight">Administrar trabajadores</DialogTitle>
          <DialogDescription>Agrega, edita o elimina trabajadores y asígnalos a una obra.</DialogDescription></DialogHeader>

        <div className="grid grid-cols-12 gap-2 items-end border border-slate-200 p-3 bg-slate-50 rounded-sm">
          <div className="col-span-4">
            <Label className="text-xs">Nombre completo</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="worker-name" className="h-8" />
          </div>
          <div className="col-span-3">
            <Label className="text-xs">Obra</Label>
            <Select value={form.project_id} onValueChange={(v) => setForm({ ...form, project_id: v })}>
              <SelectTrigger className="h-8" data-testid="worker-project"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Sin obra</SelectItem>
                {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <Label className="text-xs">Sueldo sem.</Label>
            <Input type="number" value={form.weekly_salary} onChange={(e) => setForm({ ...form, weekly_salary: e.target.value })} data-testid="worker-salary" className="h-8 font-mono-data" />
          </div>
          <div className="col-span-3">
            <Label className="text-xs">Notas</Label>
            <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="worker-notes" className="h-8" />
          </div>
          <div className="col-span-12 flex gap-2 justify-end">
            {editing && <Button variant="ghost" size="sm" onClick={reset}><X size={14} className="mr-1" />Cancelar</Button>}
            <Button size="sm" onClick={submit} data-testid="worker-save" className="bg-blue-600 hover:bg-blue-700">
              {editing ? <><Check size={14} className="mr-1" />Guardar</> : <><Plus size={14} className="mr-1" />Agregar</>}
            </Button>
          </div>
        </div>

        <div className="max-h-72 overflow-auto border border-slate-200 rounded-sm">
          <table className="w-full text-sm">
            <thead className="bg-slate-100 text-slate-600 sticky top-0">
              <tr><th className="text-left p-2">Nombre</th><th className="text-left p-2">Obra</th><th className="text-right p-2">Sueldo sem.</th><th className="p-2 w-20"></th></tr>
            </thead>
            <tbody>
              {workers.length === 0 && <tr><td colSpan={4} className="p-4 text-center text-slate-400">Sin trabajadores</td></tr>}
              {workers.map((w) => {
                const p = projects.find((x) => x.id === w.project_id);
                return (
                  <tr key={w.id} className="border-t border-slate-100" data-testid={`worker-row-${w.id}`}>
                    <td className="p-2 font-medium text-slate-800">{w.name}</td>
                    <td className="p-2 text-slate-600">{p?.name || "—"}</td>
                    <td className="p-2 text-right font-mono-data">{money(w.weekly_salary)}</td>
                    <td className="p-2 text-right whitespace-nowrap">
                      <button onClick={() => startEdit(w)} className="text-slate-500 hover:text-blue-600 mr-2" data-testid={`worker-edit-${w.id}`}><Pencil size={15} /></button>
                      <button onClick={() => setDelId(w.id)} className="text-slate-500 hover:text-red-600" data-testid={`worker-delete-${w.id}`}><Trash2 size={15} /></button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <ConfirmDialog open={!!delId} onOpenChange={(o) => !o && setDelId(null)} testid="confirm-delete-worker"
          title="Eliminar trabajador" description="El trabajador y sus registros asociados se eliminarán permanentemente."
          onConfirm={async () => { await onDelete(delId); setDelId(null); }} />
      </DialogContent>
    </Dialog>
  );
}
